import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import FormInputs from '../UI/FormInputs';
import { Modal } from "react-bootstrap";
import { isSet } from '../../utils/commonUtils';
import Buttons from "../UI/Buttons";
import InternalTaskService from '../../services/InternalTaskService';
import UserService from '../../services/UserService';
import { useLocation } from 'react-router-dom';

/** Stacked above activity-chart modals; dropdown uses menuPortal z-index in SelectListWithSearch. */
const SEND_TASK_MODAL_Z_INDEX = 1060;

const SendTaskModal = (props) => {
    const { isOpen, modalToogleHandler, Toast, Loader, navigationLink, initialTaskTitle, initialMessageBody } = props;
    const modalTitle = "Send Message";
    const location = useLocation();
    
    const [formData, setFormData] = useState({
        receiver_id: '',
        task_title: '',
        message_body: '',
        navigation_link: ''
    });
    const [validation, setValidation] = useState({});
    const [userOptions, setUserOptions] = useState([{ label: '-Select User-', value: '' }]);

    useEffect(() => {
        if (isOpen) {
            const currentPath = navigationLink || location.pathname;
            setFormData(prev => ({
                ...prev,
                navigation_link: currentPath,
                ...(initialTaskTitle !== undefined && initialTaskTitle !== null ? { task_title: initialTaskTitle } : {}),
                ...(initialMessageBody !== undefined && initialMessageBody !== null ? { message_body: initialMessageBody } : {}),
            }));
            fetchUsers();
        }
    }, [isOpen, location.pathname, navigationLink, initialTaskTitle, initialMessageBody]);

    const fetchUsers = async () => {
        try {
            Loader(true);
            const usersData = await UserService.getUserList();
            if (usersData && usersData.data) {
                let options = [{ label: '-Select User-', value: '' }];
                
                Object.keys(usersData.data).forEach(role => {
                    const users = usersData.data[role] || [];
                    users.forEach(user => {
                        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
                        options.push({
                            label: `${fullName} (${user.email})`,
                            value: user.id
                        });
                    });
                });
                
                setUserOptions(options);
            }
            Loader(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            Loader(false);
            Toast.error('Failed to load users');
        }
    };

    const inputFields = [
        {
            type: "SelectSearchList",
            label: "Select User",
            placeholder: "Select User",
            options: userOptions,
            className: "form-control form-control-solid",
            name: "receiver_id",
        },
        {
            type: "TextInput",
            label: "Task Title",
            placeholder: "Enter task title",
            className: "form-control form-control-solid",
            name: "task_title",
        },
        {
            type: "TextArea",
            label: "Message",
            placeholder: "Enter message",
            className: "form-control form-control-solid",
            name: "message_body",
            rows: 4,
        },
    ];

    const changeHandler = (value, fieldName) => {
        const updateFormData = { ...formData };
        const updateValidation = { ...validation };
        updateFormData[fieldName] = value;
        updateValidation[fieldName] = '';
        setFormData(updateFormData);
        setValidation(updateValidation);
    };

    const sendTask = async () => {
        let updateValidation = { ...validation };
        let hasError = false;

        if (!formData.receiver_id || formData.receiver_id === '') {
            updateValidation.receiver_id = 'User selection is required';
            hasError = true;
        }
        if (!formData.task_title || formData.task_title.trim() === '') {
            updateValidation.task_title = 'Task title is required';
            hasError = true;
        }
        if (!formData.message_body || formData.message_body.trim() === '') {
            updateValidation.message_body = 'Message is required';
            hasError = true;
        }

        if (hasError) {
            setValidation(updateValidation);
            Toast.error('Please fill all required fields');
            return;
        }

        Loader(true);
        try {
            const payload = {
                receiver_id: formData.receiver_id,
                task_title: formData.task_title.trim(),
                message_body: formData.message_body.trim(),
                navigation_link: formData.navigation_link || location.pathname
            };

            const response = await InternalTaskService.createTask(payload);
            if (response && (response.code === 200 || response.code === 201 || (!response.code && response))) {
                Toast.success('Message sent successfully');
                setFormData({
                    receiver_id: '',
                    task_title: '',
                    message_body: '',
                    navigation_link: navigationLink || location.pathname
                });
                setValidation({});
                modalToogleHandler();
            } else {
                Toast.error('Failed to send message');
            }
            Loader(false);
        } catch (error) {
            console.error('Error sending message:', error);
            Loader(false);
            const errorMessage = error?.response?.data?.data || error?.message || 'Failed to send message';
            Toast.error(errorMessage);
        }
    };

    return (
        <>
            {isOpen && (
                <style>{`
                  .sh-send-task-modal.modal {
                    z-index: ${SEND_TASK_MODAL_Z_INDEX} !important;
                  }
                  body.modal-open .modal-backdrop.show:last-of-type {
                    z-index: ${SEND_TASK_MODAL_Z_INDEX - 5} !important;
                  }
                `}</style>
            )}
            <Modal
                show={isOpen}
                onHide={modalToogleHandler}
                keyboard={false}
                backdrop="static"
                size="md"
                dialogClassName="hResponsive sh-send-task-modal"
                contentClassName=""
            >
                <div className="sh_modalBox">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <h6 className="text-lg mb-0 font-weight-bold">{modalTitle}</h6>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="sh_modalBody overflow-visible">
                        <div className="row">
                            {inputFields &&
                                inputFields.map((field, index) => (
                                    <div className={`col-12`} key={index}>
                                        <div className="form-group">
                                            <label className="text-gray-900 text-md opacity-75">
                                                {field.label}
                                            </label>
                                            <FormInputs
                                                id={isSet(field.id, field.name)}
                                                changeHandler={(val, e) => changeHandler(val, field.name, e)}
                                                value={formData[field.name]}
                                                {...field}
                                            />
                                            {isSet(validation[field.name], "") !== "" ? (
                                                <span className="text-danger m-1">
                                                    {isSet(validation[field.name], "")}
                                                </span>
                                            ) : ("")}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-center">
                        <Buttons
                            type='Single'
                            className="btn btn-primary"
                            clickHandler={() => sendTask()}
                            label='Send'
                            acl='any' />
                    </Modal.Footer>
                </div>
            </Modal>
        </>
    );
};

SendTaskModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    modalToogleHandler: PropTypes.func.isRequired,
    Toast: PropTypes.object.isRequired,
    Loader: PropTypes.func.isRequired,
    navigationLink: PropTypes.string,
    initialTaskTitle: PropTypes.string,
    initialMessageBody: PropTypes.string,
};

export default SendTaskModal;
